import { getCustomerGroupConfig } from "../../../../commons/libs/config";
import { getEnv } from "../../../../commons/libs/env-service";
import { ProductType } from "../../../../commons/specs/product";
import { Env } from "../../../../commons/types/env";
import useQuery, { UseQueryOptions } from "../hooks/use-query";

const queryKey = ["env"] as const;

type Options = Omit<Partial<UseQueryOptions<Env>>, "queryKey" | "queryFn">;

/**
 * Hook for accessing application environment.
 *
 * *Note: Env uses a staleTime of 60 seconds and gets refetched after that.*
 *
 * ## Usage
 * There are two patterns for using this hook:
 *
 * ### During initialization of app
 * During initialization of the app, we need to the possible `undefined` state of `query.data` directly.
 *
 *  For example:
 *  ```ts
 *  const env = useEnv();
 *
 *  if (!env.query.isSuccess) { // or `env.query.isLoading` or `!env.query.data`
 *    console.log("Loading env...");
 *  } else {
 *    console.log("env", env.query.data);
 *  }
 *  ```
 * ### After initialization
 * Since env is loaded during initialization, we can safely presume that it is available afterwards.
 * When we are deep in the app, we can use the getter properties to have easy access to the env,
 * without having to worry about the loading state in every container.
 *
 * For example:
 * ```ts
 * const env = useEnv();
 * console.log("env", env.initialized);
 * ```
 **/
const useEnv = <Type extends ProductType = ProductType>(options: Options = {}) => {
  const query = useQuery({
    ...options,
    queryKey,
    queryFn: getEnv,
    // This does not change during normal user runtime, except for manipulations from technical support.
    staleTime: 60 * 1000
  });

  return {
    query,
    // Getters
    /** Use this to get defined settings after app initialization was done. */
    get initialized() {
      if (!query.data) {
        throw new Error(`
          'useEnv': 'query.data' is still undefined! Please only use this selector after initialization.
          Please make sure that this hook is initialized during the initialization of the app. (useInitCore & useInitApp)
          `);
      } else {
        return query.data;
      }
    },
    get customerGroupConfig() {
      return getCustomerGroupConfig(this.initialized.customerGroup);
    },
    /** TODO: Get productType from Product.
     * @see BCD-6701 Integrate motorcycles into content service
     */
    get productType() {
      return this.customerGroupConfig.productTypes[0] as Type;
    }
  };
};

export default useEnv;
